/**
 * SchemaFieldMapper Component
 *
 * Maps schema fields to values for the compose_schema workflow node.
 * Allows users to populate schema objects with static values, variables, or expressions.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useSchemaStore, SchemaField, SchemaDefinition } from '@/stores/schemas/schema-store';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Hash, Type, ToggleLeft, ChevronDown, ChevronRight, AlertCircle, Wand2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AutoMatchPreviewModal } from './AutoMatchPreviewModal';
import { findAllMatches, type MatchResult } from '@/utils/autoMatchFields';

// Field mapping configuration
export interface FieldMapping {
  sourceType: 'static' | 'variable' | 'expression' | 'skip';
  value: string;
}

// Available variable from workflow
export interface AvailableVariable {
  name: string;
  type: string;
  source: string;
}

interface SchemaFieldMapperProps {
  schemaId: string;
  fieldMappings: Record<string, FieldMapping>;
  onChange: (mappings: Record<string, FieldMapping>) => void;
  availableVariables: AvailableVariable[];
  className?: string;
}

// Type icon component
const TypeIcon: React.FC<{ type: SchemaField['type']; className?: string }> = ({ type, className }) => {
  switch (type) {
    case 'NUMBER':
      return <Hash className={cn('h-3.5 w-3.5 text-blue-500', className)} />;
    case 'BOOLEAN':
      return <ToggleLeft className={cn('h-3.5 w-3.5 text-purple-500', className)} />;
    case 'STRING':
    default:
      return <Type className={cn('h-3.5 w-3.5 text-green-500', className)} />;
  }
};

// Individual field mapping row
interface FieldMappingRowProps {
  field: SchemaField;
  mapping: FieldMapping;
  onChange: (mapping: FieldMapping) => void;
  availableVariables: AvailableVariable[];
  depth?: number;
}

const FieldMappingRow: React.FC<FieldMappingRowProps> = ({
  field,
  mapping,
  onChange,
  availableVariables,
  depth = 0,
}) => {
  const paddingLeft = depth * 16;

  const handleSourceTypeChange = (sourceType: FieldMapping['sourceType']) => {
    onChange({ sourceType, value: '' });
  };

  const handleValueChange = (value: string) => {
    onChange({ ...mapping, value });
  };

  // Check if value is valid based on field type
  const isValid = useMemo(() => {
    // Skip is always valid - field will be preserved from existing record
    if (mapping.sourceType === 'skip') return true;

    if (!mapping.value && field.required) return false;
    if (!mapping.value) return true; // Optional empty is OK

    if (mapping.sourceType === 'static') {
      switch (field.type) {
        case 'NUMBER':
          return !isNaN(parseFloat(mapping.value));
        case 'BOOLEAN':
          return ['true', 'false', '1', '0', 'yes', 'no'].includes(mapping.value.toLowerCase());
        default:
          return true;
      }
    }
    return true; // Variables and expressions are validated at runtime
  }, [mapping, field]);

  return (
    <div
      className="grid grid-cols-12 gap-2 items-center py-1.5 border-b border-gray-100 last:border-b-0"
      style={{ paddingLeft }}
    >
      {/* Field name and type */}
      <div className="col-span-4 flex items-center gap-1.5 min-w-0">
        <TypeIcon type={field.type} />
        <span className="text-sm font-medium truncate" title={field.name}>
          {field.name}
        </span>
        {field.required && (
          <span className="text-red-500 text-xs">*</span>
        )}
      </div>

      {/* Source type selector */}
      <div className="col-span-3">
        <Select
          value={mapping.sourceType}
          onValueChange={handleSourceTypeChange}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="static">Static</SelectItem>
            <SelectItem value="variable">Variable</SelectItem>
            <SelectItem value="expression">Expression</SelectItem>
            <SelectItem value="skip">Skip (Preserve)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Value input based on source type */}
      <div className="col-span-5 flex items-center gap-1">
        {mapping.sourceType === 'skip' ? (
          <span className="text-xs text-gray-500 italic">
            Field will be preserved
          </span>
        ) : mapping.sourceType === 'variable' ? (
          <Select
            value={mapping.value}
            onValueChange={handleValueChange}
          >
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Select variable..." />
            </SelectTrigger>
            <SelectContent>
              {availableVariables.map((v) => (
                <SelectItem key={v.name} value={v.name}>
                  <span className="flex items-center gap-1">
                    <span>{v.name}</span>
                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                      {v.source}
                    </Badge>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={mapping.value}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={
              mapping.sourceType === 'expression'
                ? '{{variable}} + 1'
                : field.type === 'NUMBER'
                  ? '0'
                  : field.type === 'BOOLEAN'
                    ? 'true/false'
                    : 'value...'
            }
            className={cn(
              'h-8 text-xs',
              !isValid && 'border-red-300 focus:ring-red-500'
            )}
          />
        )}

        {!isValid && mapping.sourceType !== 'skip' && (
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
        )}
      </div>
    </div>
  );
};

export const SchemaFieldMapper: React.FC<SchemaFieldMapperProps> = ({
  schemaId,
  fieldMappings,
  onChange,
  availableVariables,
  className,
}) => {
  const { schemas, fetchSchemas, getSchema, isLoading } = useSchemaStore();
  const cachedSchema = schemas.find((s) => s.id === schemaId);

  // On-demand fetch: if schemaId is set but not in the cache, fetch it directly
  const [fetchedSchema, setFetchedSchema] = useState<SchemaDefinition | null>(null);
  const [isFetchingSchema, setIsFetchingSchema] = useState(false);

  useEffect(() => {
    if (schemaId && !cachedSchema && !fetchedSchema && !isFetchingSchema) {
      setIsFetchingSchema(true);
      getSchema(schemaId).then((result) => {
        if (result) setFetchedSchema(result);
        setIsFetchingSchema(false);
      });
    }
  }, [schemaId, cachedSchema, fetchedSchema, isFetchingSchema, getSchema]);

  // Reset fetched schema if schemaId changes
  useEffect(() => {
    setFetchedSchema(null);
  }, [schemaId]);

  const schema = cachedSchema || fetchedSchema;

  // Auto Match state
  const [isAutoMatchOpen, setIsAutoMatchOpen] = useState(false);
  const [proposedMatches, setProposedMatches] = useState<MatchResult[]>([]);

  // Fetch schemas if not loaded
  useEffect(() => {
    if (schemas.length === 0) {
      fetchSchemas();
    }
  }, [schemas.length, fetchSchemas]);

  // Flatten nested fields into dot-path list (for future nested object support)
  const flattenedFields = useMemo(() => {
    if (!schema?.fields) return [];

    // For now, schema.fields is flat, but this supports future nesting
    const flatten = (fields: SchemaField[], prefix: string = ''): SchemaField[] => {
      return fields.flatMap((field) => {
        const path = prefix ? `${prefix}.${field.name}` : field.name;
        // If field had nested fields (future), we'd recurse here
        return [{ ...field, name: path }];
      });
    };

    return flatten(schema.fields);
  }, [schema]);

  // Update a single field mapping
  const updateMapping = (fieldPath: string, mapping: FieldMapping) => {
    onChange({
      ...fieldMappings,
      [fieldPath]: mapping,
    });
  };

  // Get mapping for a field, with default
  const getMapping = (fieldPath: string): FieldMapping => {
    return fieldMappings[fieldPath] || { sourceType: 'static', value: '' };
  };

  // Handle Auto Match button click
  const handleAutoMatch = () => {
    if (flattenedFields.length === 0 || availableVariables.length === 0) {
      return;
    }
    const matches = findAllMatches(flattenedFields, availableVariables);
    setProposedMatches(matches);
    setIsAutoMatchOpen(true);
  };

  // Handle applying selected matches from the modal
  const handleApplyMatches = (selectedMatches: MatchResult[]) => {
    const newMappings = { ...fieldMappings };

    for (const match of selectedMatches) {
      if (match.selected && match.sourcePath) {
        newMappings[match.schemaField] = {
          sourceType: 'variable',
          value: match.sourcePath,
        };
      }
    }

    onChange(newMappings);
    setIsAutoMatchOpen(false);
  };

  if (isLoading || isFetchingSchema) {
    return (
      <div className={cn('text-sm text-gray-500 p-4', className)}>
        Loading schema...
      </div>
    );
  }

  if (!schema) {
    return (
      <div className={cn('text-sm text-gray-500 p-4', className)}>
        Schema not found. Please select a schema first.
      </div>
    );
  }

  if (flattenedFields.length === 0) {
    return (
      <div className={cn('text-sm text-gray-500 p-4', className)}>
        This schema has no fields defined.
      </div>
    );
  }

  return (
    <div className={cn('space-y-1', className)}>
      {/* Auto Match Button */}
      {availableVariables.length > 0 && (
        <div className="flex justify-end pb-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleAutoMatch}
            className="text-xs"
          >
            <Wand2 className="h-3.5 w-3.5 mr-1.5" />
            Auto Match
          </Button>
        </div>
      )}

      {/* Header */}
      <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 pb-1 border-b">
        <div className="col-span-4">Field</div>
        <div className="col-span-3">Source</div>
        <div className="col-span-5">Value</div>
      </div>

      {/* Field mappings */}
      <div className="max-h-[300px] overflow-y-auto">
        {flattenedFields.map((field) => (
          <FieldMappingRow
            key={field.name}
            field={field}
            mapping={getMapping(field.name)}
            onChange={(m) => updateMapping(field.name, m)}
            availableVariables={availableVariables}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-gray-500 pt-2 border-t">
        <span className="flex items-center gap-1">
          <Type className="h-3 w-3 text-green-500" /> String
        </span>
        <span className="flex items-center gap-1">
          <Hash className="h-3 w-3 text-blue-500" /> Number
        </span>
        <span className="flex items-center gap-1">
          <ToggleLeft className="h-3 w-3 text-purple-500" /> Boolean
        </span>
        <span className="text-red-500">* Required</span>
      </div>

      {/* Auto Match Preview Modal */}
      <AutoMatchPreviewModal
        open={isAutoMatchOpen}
        onOpenChange={setIsAutoMatchOpen}
        matches={proposedMatches}
        onApply={handleApplyMatches}
      />
    </div>
  );
};

export default SchemaFieldMapper;
